/**
 * useStoreDocsByPaths
 *
 * Subscribe to multiple Firestore documents by their full paths.
 * Returns a Valtio-backed snapshot of `{ data, loading, errors }`.
 *
 * Each path gets its own `createDocumentStore` (registry-deduped). When the
 * caller's path list changes, only the diff is applied — new paths connect,
 * removed paths disconnect, existing paths are left alone.
 *
 * Subscription fan-out is N for N paths, matching Firestore's onSnapshot
 * model. If callers need bounded subscription cost, switch to a one-shot
 * `getDocs` instead.
 *
 * @example
 * ```ts
 * const { data, loading } = useStoreDocsByPaths<EntityRecord>(
 *   recordIds.map((id) => `entities/${entityId}/records/${id}`),
 * );
 *
 * const records = [...data.values()];
 * ```
 */

import { useEffect, useMemo, useRef } from 'react';
import { proxy, snapshot, subscribe, useSnapshot } from 'valtio';
import { proxyMap } from 'valtio/utils';
import { createDocumentStore, type DocumentStore } from './singleton/createDocumentStore';

export type UseStoreDocsByPathsReturn<T> = {
  data: Map<string, T>;
  loading: boolean;
  errors: Map<string, Error>;
};

type AggregateState<T> = {
  data: Map<string, T>;
  errors: Map<string, Error>;
  loading: boolean;
};

type Connected<T extends Record<string, unknown>> = {
  store: DocumentStore<T>;
  unsubscribeState: () => void;
};

export function useStoreDocsByPaths<T extends Record<string, unknown>>(
  paths: string[],
): UseStoreDocsByPathsReturn<T> {
  const stableKey = useMemo(() => {
    const deduped = [...new Set(paths)].sort();
    return deduped.join('|');
  }, [paths]);

  const stablePaths = useMemo(
    () => (stableKey === '' ? [] : stableKey.split('|')),
    [stableKey],
  );

  const aggregateRef = useRef<AggregateState<T> | null>(null);
  if (aggregateRef.current === null) {
    aggregateRef.current = proxy<AggregateState<T>>({
      data: proxyMap<string, T>(),
      errors: proxyMap<string, Error>(),
      loading: true,
    });
  }
  const aggregate = aggregateRef.current;

  const connectedRef = useRef<Map<string, Connected<T>>>(new Map());

  useEffect(() => {
    const connected = connectedRef.current;
    const next = new Set(stablePaths);

    for (const [path, entry] of connected) {
      if (!next.has(path)) {
        entry.unsubscribeState();
        entry.store.disconnect();
        connected.delete(path);
        aggregate.data.delete(path);
        aggregate.errors.delete(path);
      }
    }

    const recomputeLoading = () => {
      let anyLoading = false;
      for (const entry of connected.values()) {
        if (entry.store.state.loading) {
          anyLoading = true;
          break;
        }
      }
      aggregate.loading = anyLoading;
    };

    for (const path of stablePaths) {
      if (connected.has(path)) continue;
      const store = createDocumentStore<T>(path);

      const sync = () => {
        const snap = snapshot(store.state);
        const value = snap.data;
        if (value !== null) {
          aggregate.data.set(path, value as T);
          aggregate.errors.delete(path);
        } else {
          aggregate.data.delete(path);
        }
        recomputeLoading();
      };

      const unsubscribeState = subscribe(store.state, sync);
      connected.set(path, { store, unsubscribeState });

      store.connect({
        onError: (error) => aggregate.errors.set(path, error),
      });
      sync();
    }

    if (connected.size === 0) {
      aggregate.loading = false;
    } else {
      recomputeLoading();
    }
  }, [stablePaths, aggregate]);

  useEffect(() => {
    const connected = connectedRef.current;
    return () => {
      for (const entry of connected.values()) {
        entry.unsubscribeState();
        entry.store.disconnect();
      }
      connected.clear();
    };
  }, []);

  const snap = useSnapshot(aggregate);
  return {
    data: snap.data as Map<string, T>,
    loading: snap.loading,
    errors: snap.errors as Map<string, Error>,
  };
}
